import { useCallback, useContext, useEffect, useRef } from "react";
import type { FoldStep } from "~/components/ui/bottom-sheet";
import { useBreakpoint } from "~/hooks/useBreakpoint";
import { MapLayoutContext } from "./context";

export type PanelPosition = "hidden" | "collapsed" | "preview" | "expanded";

const TO_FOLD_STEP: Record<PanelPosition, FoldStep> = {
  hidden: "closed",
  collapsed: "folded",
  preview: "peek",
  expanded: "open",
};

const TO_PANEL_POSITION: Record<FoldStep, PanelPosition> = {
  closed: "hidden",
  folded: "collapsed",
  peek: "preview",
  open: "expanded",
};

/** Internal hook for layout primitives (Panel, Controls, Canvas). */
export function useMapLayout() {
  const context = useContext(MapLayoutContext);
  if (!context) {
    throw new Error("useMapLayout must be used within Map.Canvas");
  }
  return context;
}

/**
 * Public hook for panel position.
 *
 * On desktop (lg+), position is always `"expanded"` and `snapTo` is a no-op.
 *
 * @example
 * const { position, snapTo } = usePanel();
 * const details = toDetailLevel(position);
 */
export function usePanel(): { position: PanelPosition; snapTo: (position: PanelPosition) => void } {
  const { sheetStep, setSheetStep } = useMapLayout();
  const { below } = useBreakpoint();
  const hasBottomSheet = below("lg");

  const snapTo = useCallback(
    (p: PanelPosition) => {
      if (!hasBottomSheet) return;
      setSheetStep(TO_FOLD_STEP[p]);
    },
    [hasBottomSheet, setSheetStep],
  );

  if (!hasBottomSheet) {
    return { position: "expanded", snapTo };
  }

  const position: PanelPosition = sheetStep ? TO_PANEL_POSITION[sheetStep] : "hidden";
  return { position, snapTo };
}

/**
 * Declaratively sets the panel position while a condition is active.
 * Restores the previous position when the condition becomes false or on unmount.
 *
 * Multiple hooks can be active simultaneously (LIFO stacking).
 *
 * @example
 * usePanelPosition("preview", !!selectedPlace);
 * usePanelPosition("collapsed", isPlacingMode);
 */
export function usePanelPosition(position: PanelPosition, active: boolean) {
  const { sheetStep, setSheetStep } = useMapLayout();
  const step = TO_FOLD_STEP[position];
  const previousStepRef = useRef<FoldStep | null>(null);
  const sheetStepRef = useRef(sheetStep);
  sheetStepRef.current = sheetStep;

  useEffect(() => {
    if (active && previousStepRef.current === null) {
      previousStepRef.current = sheetStepRef.current;
      setSheetStep(step);
    } else if (!active && previousStepRef.current !== null) {
      setSheetStep(previousStepRef.current);
      previousStepRef.current = null;
    }

    return () => {
      if (previousStepRef.current !== null) {
        setSheetStep(previousStepRef.current);
        previousStepRef.current = null;
      }
    };
  }, [active, step, setSheetStep]);
}
